/**
 * @fileoverview Main CRUD component for Planificador Tareas
 * Manages production task planning with drag & drop interface
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge
} from 'reactstrap';
import TableContainer from '../../components/Common/DataTableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { DragDropInterface } from '../../components/DragDropInterface';
import { usePlanificador } from '../../hooks/usePlanificador';
import { useDragDrop } from '../../hooks/useDragDrop';
import { PlanCentroCostoExpanded } from '../../types/collections/PlanCentroCosto';
import { CentroCostoExpanded } from '../../types/collections/CentroCosto';
import { CentroCostoPeso } from '../../types/collections/CentroCostoPeso';

/**
 * Main PlanificadorTareas component
 * Provides CRUD operations for production task planning
 */
const PlanificadorTareas = () => {
  // Hooks
  const {
    planes,
    centrosCosto,
    loading,
    error,
    fetchPlanes,
    fetchCentrosCosto,
    createPlan,
    updatePlan,
    deletePlan,
    restorePlan,
    clearError
  } = usePlanificador();

  const {
    secuencia,
    centrosDisponibles,
    agregarACSecuencia,
    removerDeSecuencia,
    reordenarSecuencia,
    limpiarSecuencia,
    establecerSecuencia,
    validarSecuencia,
    actualizarPeso
  } = useDragDrop(centrosCosto);

  // Local state
  const [modalAdd, setModalAdd] = useState(false);
  const [modalError, setModalError] = useState("");
  const [saving, setSaving] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  
  // Estados para confirmación de eliminar y restaurar
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [planToDeactivate, setPlanToDeactivate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Para confirmación de restaurar
  const [showRestoreAlert, setShowRestoreAlert] = useState(false);
  const [planToRestore, setPlanToRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [form, setForm] = useState({
    id: null,
    nombre: "",
    centro_costo_peso: [] // Array of CentroCostoPeso objects
  });

  // Effects
  useEffect(() => {
    fetchPlanes();
    fetchCentrosCosto();
  }, [fetchPlanes, fetchCentrosCosto]);



  useEffect(() => {
    if (modalAdd) {
      clearError();
    }
  }, [modalAdd, clearError]);

  // Handlers
  const handleAdd = () => {
    setForm({ id: null, nombre: "", centro_costo_peso: [] });
    limpiarSecuencia();
    setModalError("");
    setModalAdd(true);
  };

  // Function to reload data
  const reloadPlanes = () => {
    fetchPlanes();
  };

  const handleEdit = (plan) => {
    // Verificar si tenemos datos expandidos
    let centroCostoPesoData = plan.centro_costo_peso || [];
    let expandedData = plan.expand?.centro_costo_peso || [];
    
    setForm({
      id: plan.id,
      nombre: plan.nombre || "",
      centro_costo_peso: centroCostoPesoData
    });
    
    // Si tenemos datos expandidos, usarlos para la secuencia
    if (expandedData.length > 0) {
      // Los datos ya vienen expandidos del servidor, usarlos directamente
      establecerSecuencia(expandedData);
    }
    // Si no, usar los IDs pero expandir los datos
    else if (centroCostoPesoData.length > 0) {
      // Expandir los datos usando los centros de costo disponibles
      const expandedData = centroCostoPesoData.map(item => {
        if (typeof item === 'string') {
          // Si es un ID, buscar el centro de costo correspondiente
          const centro = centrosCosto.find(c => c.id === item);
          return {
            id: `temp_${Date.now()}`,
            centro_costo: item,
            peso: 0,
            expand: centro ? { centro_costo: centro } : undefined
          };
        } else {
          // Si ya es un objeto, expandir si es necesario
          if (!item.expand?.centro_costo && item.centro_costo) {
            const centro = centrosCosto.find(c => c.id === item.centro_costo);
            return {
              ...item,
              expand: centro ? { centro_costo: centro } : item.expand
            };
          }
          return item;
        }
      });
      establecerSecuencia(expandedData);
    }
    // Si no hay datos, limpiar la secuencia
    else {
      limpiarSecuencia();
    }
    
    setModalError("");
    setModalAdd(true);
  };

  const handleRemove = (plan) => {
    setPlanToDeactivate(plan);
    setShowDeleteAlert(true);
    // Hacer scroll hacia arriba para mostrar el alert
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };
  
  // Cancelar eliminación
  const cancelRemove = () => {
    setShowDeleteAlert(false);
    setPlanToDeactivate(null);
  };
  
  // Confirmar eliminación
  const confirmRemove = async () => {
    if (!planToDeactivate) return;
    setDeleting(true);
    try {
      await deletePlan(planToDeactivate.id);
      fetchPlanes();
      setShowDeleteAlert(false);
      setPlanToDeactivate(null);
    } catch (err) {
      console.error('Error deleting plan:', err);
    }
    setDeleting(false);
  };

  const handleRestore = (plan) => {
    setPlanToRestore(plan);
    setShowRestoreAlert(true);
    // Hacer scroll hacia arriba para mostrar el alert
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };
  
  // Cancelar restauración
  const cancelRestore = () => {
    setShowRestoreAlert(false);
    setPlanToRestore(null);
  };
  
  // Confirmar restauración
  const confirmRestore = async () => {
    if (!planToRestore) return;
    setRestoring(true);
    try {
      await restorePlan(planToRestore.id);
      fetchPlanes();
      setShowRestoreAlert(false);
      setPlanToRestore(null);
    } catch (err) {
      console.error('Error restoring plan:', err);
    }
    setRestoring(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!form.nombre.trim()) {
      setModalError("El nombre del plan es requerido");
      return;
    }

    if (secuencia.length === 0) {
      setModalError("Debe agregar al menos un centro de costo a la secuencia");
      return;
    }

    setSaving(true);
    setModalError("");

    try {
      // Validate weights before saving
      const validation = validarSecuencia();
      if (!validation.isValid) {
        setModalError(validation.errors.join('. '));
        
        // Scroll to first error field
        if (validation.firstErrorIndex >= 0) {
          setTimeout(() => {
            const firstErrorItem = document.getElementById(`proceso-item-${secuencia[validation.firstErrorIndex].id}`);
            if (firstErrorItem) {
              // Add error class for visual feedback
              firstErrorItem.classList.add('has-error');
              
              // Scroll to the error item
              firstErrorItem.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
              
              // Focus on the weight input of the first error item
              const weightInput = document.getElementById(`peso-input-${secuencia[validation.firstErrorIndex].id}`);
              if (weightInput) {
                weightInput.focus();
                weightInput.select();
              }
              
              // Remove error class after 3 seconds
              setTimeout(() => {
                firstErrorItem.classList.remove('has-error');
              }, 3000);
            }
          }, 100);
        }
        return;
      }

      const data = {
        nombre: form.nombre.trim().toUpperCase(), // Convertir a mayúsculas
        centro_costo_peso: secuencia, // Array of CentroCostoPeso objects
        activo: true
      };

      if (form.id) {
        // Actualizar plan existente
        await updatePlan(form.id, data);
      } else {
        // Crear nuevo plan
        await createPlan(data);
      }

      fetchPlanes();
      setModalAdd(false);
      limpiarSecuencia();
    } catch (err) {
      console.error('Error saving plan:', err);
      if (err.data?.data?.nombre?.code === "validation_not_unique") {
        setModalError(
          `El plan con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
        );
      } else {
        setModalError(form.id ? "Error al actualizar plan" : "Error al crear plan");
      }
    } finally {
      setSaving(false);
    }
  };


  const handleSecuenciaChange = useCallback((newSecuencia) => {
    // Update the sequence in the useDragDrop hook
    establecerSecuencia(newSecuencia);
  }, [establecerSecuencia]);

  const handleWeightChange = useCallback((index, peso) => {
    // Update the weight of a specific item in the sequence
    actualizarPeso(index, peso);
  }, [actualizarPeso]);

  const handleReorder = useCallback((fromIndex, toIndex) => {
    reordenarSecuencia(fromIndex, toIndex);
  }, [reordenarSecuencia]);

  // Filtrar planes activos e inactivos
  const planesActivos = useMemo(() => planes.filter(plan => plan.activo === true), [planes]);
  const planesInactivos = useMemo(() => planes.filter(plan => plan.activo === false), [planes]);

  // Table columns configuration for active plans
  const columnsActivos = useMemo(() => [
    {
      header: 'Nombre',
      accessorKey: 'nombre',
      cell: info => (
        <span className="fw-bold">{info.getValue() || 'Sin nombre'}</span>
      ),
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: 'Secuencia',
      accessorKey: 'centro_costo_peso',
      cell: info => {
        const plan = info.row.original;
        
        // Handle both old format (string[]) and new format (CentroCostoPeso[])
        const centrosCostoData = plan.centro_costo_peso || [];
        const expandedData = plan.expand?.centro_costo_peso || [];
        

        
        return (
          <div>
            {centrosCostoData.length > 0 ? (
              <div className="d-flex flex-wrap gap-1">
                {centrosCostoData.map((centroData, index) => {
                  // Obtener el objeto expandido correspondiente a este ID
                  const expandedItem = plan.expand?.centro_costo_peso?.[index];

                  
                  // Si tenemos datos expandidos, usarlos
                  if (expandedItem && expandedItem.expand?.centro_costo) {
                    const peso = expandedItem.peso || 0;
                    const centro = expandedItem.expand.centro_costo;
                    

                    
                    // Acceder a maquina, encargado y proceso de forma segura
                    const maquina = centro.expand?.maquina || 
                                   (typeof centro.maquina === 'object' ? centro.maquina : null);
                    const encargado = centro.expand?.encargado || 
                                     (typeof centro.encargado === 'object' ? centro.encargado : null);
                    const proceso = centro.expand?.proceso || 
                                   (typeof centro.proceso === 'object' ? centro.proceso : null);
                    
                    return (
                      <div 
                        key={`${expandedItem.id}-${index}`} 
                        className="mb-1 p-2 border rounded bg-light"
                        title={`Encargado: ${encargado?.name || "Sin encargado"}`}
                      >
                        <div className="fw-bold text-primary">
                          {index + 1}. {centro.nombre}
                          {peso > 0 && (
                            <span className="ms-2 badge bg-info">
                              <i className="fas fa-weight me-1"></i>
                              {peso}%
                            </span>
                          )}
                        </div>
                        <div className="small text-muted">
                          <span className="me-2">
                            <i className="fas fa-cog me-1"></i>
                            {maquina?.nombre || "Sin máquina"}
                          </span>
                          <span>
                            <i className="fas fa-cogs me-1"></i>
                            {proceso?.nombre || ""}
                          </span>
                        </div>
                      </div>
                    );
                  } 
                  // Si no tenemos datos expandidos, intentar buscar el centro por ID
                  else {
                    // Buscar el centro de costo por ID (formato antiguo)
                    const centro = centrosCosto.find(c => c.id === centroData);
                    
                    if (!centro) {

                      return (
                        <div key={`${centroData}-${index}`} className="mb-1 p-2 border rounded bg-light">
                          <div className="fw-bold text-primary">
                            {index + 1}. Centro no encontrado
                          </div>
                          <div className="small text-muted">
                            <pre className="small">ID: {centroData}</pre>
                          </div>
                        </div>
                      );
                    }
                    
                    // Acceder a maquina y encargado de forma segura
                    const maquina = centro.expand?.maquina;
                    const encargado = centro.expand?.encargado;
                    
                    return (
                      <div 
                        key={`${centro.id}-${index}`} 
                        className="mb-1 p-2 border rounded bg-light"
                        title={`Encargado: ${encargado?.name || "Sin encargado"}`}
                      >
                        <div className="fw-bold text-primary">
                          {index + 1}. {centro.nombre}
                        </div>
                        <div className="small text-muted">
                          <span className="me-2">
                            <i className="fas fa-cog me-1"></i>
                            {maquina?.nombre || "Sin máquina"}
                          </span>
                          <span>
                            <i className="fas fa-cogs me-1"></i>
                            {centro.expand?.proceso?.nombre || ""}
                          </span>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            ) : (
              <span className="text-muted">Sin secuencia</span>
            )}
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: 'Estado',
      accessorKey: 'activo',
      cell: info => (
        <Badge color={info.getValue() ? 'success' : 'secondary'}>
          {info.getValue() ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: 'Acciones',
      accessorKey: 'id',
      cell: info => {
        const plan = info.row.original;
        return (
          <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
            <Button color="warning" size="sm" className="me-2" onClick={() => handleEdit(plan)} disabled={plan.activo === false}>
              <i className="fas fa-pen me-1"></i> Editar
            </Button>
            {plan.activo ? (
              <Button color="danger" size="sm" onClick={() => handleRemove(plan)}>
                <i className="fas fa-eraser me-1"></i> Eliminar
              </Button>
            ) : (
              <Button color="success" size="sm" onClick={() => handleRestore(plan)}>
                <i className="fas fa-undo me-1"></i> Restaurar
              </Button>
            )}
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      meta: { style: { width: "1%", whiteSpace: "nowrap" } },
    },
  ], [centrosCosto]);
  
  // Table columns configuration for inactive plans
  const columnsInactivos = useMemo(() => [
    {
      header: 'Nombre',
      accessorKey: 'nombre',
      cell: info => (
        <span className="fw-bold">{info.getValue() || 'Sin nombre'}</span>
      ),
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: 'Secuencia',
      accessorKey: 'centro_costo_peso',
      cell: info => {
        const plan = info.row.original;
        
        // Handle both old format (string[]) and new format (CentroCostoPeso[])
        const centrosCostoData = plan.centro_costo_peso || [];
        const expandedData = plan.expand?.centro_costo_peso || [];
        
        return (
          <div>
            {centrosCostoData.length > 0 ? (
              <div className="d-flex flex-wrap gap-1">
                {expandedData.map((item, index) => {
                  const centroCosto = item.expand?.centro_costo;
                  if (!centroCosto) return null;
                  
                  return (
                    <Badge
                      key={`${plan.id}-${index}`}
                      color="light"
                      className="border"
                      style={{ fontSize: '0.8rem' }}
                    >
                      {centroCosto.nombre} ({item.peso || 0}%)
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <span className="text-muted">Sin secuencia</span>
            )}
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: 'Estado',
      accessorKey: 'activo',
      cell: info => (
        <Badge color={info.getValue() ? 'success' : 'secondary'}>
          {info.getValue() ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: 'Acciones',
      accessorKey: 'id',
      cell: info => {
        const plan = info.row.original;
        return (
          <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
            <Button color="success" size="sm" onClick={() => handleRestore(plan)}>
              <i className="fas fa-undo me-1"></i> Restaurar
            </Button>
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      meta: { style: { width: "1%", whiteSpace: "nowrap" } },
    },
  ], [centrosCosto]);

  if (loading && planes.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
        <span className="ms-2">Cargando planificador de tareas...</span>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs maintitle="Dashboard" title="Producción" breadcrumbItem="Planificador Tareas" />

        <Row>
          <Col>
            <Card>
              <CardBody>
                <CardTitle className="h4 d-flex justify-content-between align-items-center">
                  <i className="fas fa-tasks me-2"></i>
                  Planes de Trabajo
                  <div>
                    <Button color="primary" size="sm" onClick={handleAdd}>
                      <i className="fas fa-plus me-1"></i> Agregar
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      className="ms-2"
                      title="Recargar tabla"
                      onClick={reloadPlanes}
                    >
                      <i className="fas fa-sync-alt"></i>
                    </Button>
                  </div>
                </CardTitle>
                
                <h5 className="mb-3">Planes Activos</h5>

                {error && (
                  <Alert color="danger" className="mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                    <Button close onClick={clearError} />
                  </Alert>
                )}
                
                {/* Alerta de confirmación para eliminar */}
                {showDeleteAlert && planToDeactivate && (
                  <Alert
                    color="warning"
                    isOpen={showDeleteAlert}
                    toggle={cancelRemove}
                    fade={true}
                    transition={{ timeout: 150 }}
                    className="d-flex align-items-center justify-content-between mb-3"
                  >
                    <div>
                      <strong>¿Está seguro?</strong> Esto desactivará el plan <b>{planToDeactivate.nombre}</b>.
                    </div>
                    <div>
                      <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                        {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                      </Button>
                      <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                        Cancelar
                      </Button>
                    </div>
                  </Alert>
                )}
                
                {/* Alerta de confirmación para restaurar */}
                {showRestoreAlert && planToRestore && (
                  <Alert
                    color="info"
                    isOpen={showRestoreAlert}
                    toggle={cancelRestore}
                    fade={true}
                    transition={{ timeout: 150 }}
                    className="d-flex align-items-center justify-content-between mb-3"
                  >
                    <div>
                      <strong>¿Está seguro?</strong> Esto restaurará el plan <b>{planToRestore.nombre}</b>.
                    </div>
                    <div>
                      <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                        {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                      </Button>
                      <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                        Cancelar
                      </Button>
                    </div>
                  </Alert>
                )}

                <div className="table-rep-plugin" style={{ overflow: "visible" }}>
                  <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                    <TableContainer
                      columns={columnsActivos}
                      data={planesActivos}
                      pageSize={10}
                      searchable={true}
                      filterable={true}
                      loading={loading}
                      tableClass="table-bordered dt-responsive w-100 dataTable no-footer dtr-inline"
                    />
                  </div>
                </div>
                
                {planesInactivos.length > 0 && (
                  <>
                    <h5 className="mt-5 mb-3">Planes Inactivos</h5>
                    <div className="table-rep-plugin" style={{ overflow: "visible" }}>
                      <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                        <TableContainer
                          columns={columnsInactivos}
                          data={planesInactivos}
                          pageSize={10}
                          searchable={true}
                          filterable={true}
                          loading={loading}
                          tableClass="table-bordered dt-responsive w-100 dataTable no-footer dtr-inline"
                        />
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Add/Edit Modal */}
        <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="xl">
          <ModalHeader toggle={() => setModalAdd(!modalAdd)}>
            {form.id ? "Editar Plan de Trabajo" : "Nuevo Plan de Trabajo"}
          </ModalHeader>
          <Form onSubmit={handleSave}>
            <ModalBody>
              {modalError && (
                <Alert color="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {modalError}
                </Alert>
              )}

              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label for="nombre">Nombre del Plan *</Label>
                    <Input
                      id="nombre"
                      type="text"
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      placeholder="Ingrese el nombre del plan de trabajo"
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col>
                  <Label className="fw-bold mb-2">
                    <i className="fas fa-sort-numeric-down me-2"></i>
                    Secuencia de Procesos *
                  </Label>
                  <DragDropInterface
                    centrosCosto={centrosCosto}
                    secuencia={secuencia}
                    onSecuenciaChange={handleSecuenciaChange}
                    onReorder={handleReorder}
                    onWeightChange={handleWeightChange}
                  />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setModalAdd(!modalAdd)}>
                Cancelar
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : null}
                {saving ? "Guardando..." : (form.id ? "Actualizar" : "Guardar")}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

      </div>
    </div>
  );
};

export default PlanificadorTareas;
